/**
 * perfProbe — React bindings + enablement for the chat frontend perf probe.
 *
 * Goal: a robust, reproducible way to measure WHY long conversations get laggy —
 * which components re-render without need, and how expensive each render is —
 * without adding any cost to a normal production build.
 *
 * Zero-cost when disabled: the probe is OFF unless explicitly enabled. When off,
 *   - useRenderTracker() returns before invoking its (thunk) argument, so the
 *     caller never even allocates the watched-values object, and
 *   - <ProfiledRegion> renders its children directly, adding no React.Profiler
 *     node to the tree.
 *
 * Enabling (any one):
 *   - build with REACT_APP_PERF_PROBE=true (the measurement build), or
 *   - append ?perfProbe=1 to the URL (lets you probe an existing build, e.g. to
 *     diagnose a specific slow user session), or
 *   - localStorage.setItem('perfProbe','1').
 *
 * When enabled, window.__perfProbe__ exposes summary()/reset()/mark()/dump()
 * which the Playwright harness drives and reads.
 */
import React from 'react';
import { RenderRecorder, ProbeSummary } from './perfProbeCore';

export * from './perfProbeCore';

let recorder: RenderRecorder | null = null;

export function isPerfProbeEnabled(): boolean {
  // Build-time opt-in (statically replaced by CRA). Wrapped in try/catch so a
  // missing process shim can never throw at module init.
  try {
    if (process.env.REACT_APP_PERF_PROBE === 'true') return true;
  } catch {
    /* no-op */
  }
  if (typeof window === 'undefined') return false;
  try {
    const q = new URLSearchParams(window.location.search);
    const qv = q.get('perfProbe');
    if (qv === '1' || qv === 'true') return true;
    if (window.localStorage && window.localStorage.getItem('perfProbe') === '1') return true;
  } catch {
    /* no-op */
  }
  return false;
}

export interface PerfProbeWindowApi {
  summary: () => ProbeSummary;
  reset: () => void;
  mark: (label: string) => void;
  dump: () => string;
  enabled: true;
}

declare global {
  interface Window {
    __perfProbe__?: PerfProbeWindowApi;
  }
}

/** Initialize the probe once, before React renders. No-op unless enabled. */
export function initPerfProbe(): void {
  if (recorder || typeof window === 'undefined') return;
  if (!isPerfProbeEnabled()) return;
  recorder = new RenderRecorder();
  window.__perfProbe__ = {
    summary: () => recorder!.summarize(),
    reset: () => recorder!.reset(),
    mark: (label: string) => recorder!.mark(label),
    dump: () => JSON.stringify(recorder!.summarize()),
    enabled: true,
  };
  // eslint-disable-next-line no-console
  console.info('[perfProbe] enabled — window.__perfProbe__ is available');
}

/** Test-only: tear down the active recorder. */
export function __resetPerfProbeForTests(): void {
  recorder = null;
  if (typeof window !== 'undefined') delete window.__perfProbe__;
}

export function getRecorder(): RenderRecorder | null {
  return recorder;
}

/**
 * Count a render of `name`. Pass a thunk returning the values the component
 * reads to render; it is only invoked when the probe is enabled, so disabled
 * builds pay nothing (not even the object allocation).
 *
 *   useRenderTracker('MessageList', () => ({ messageHistory, messageBeingGenerated }));
 */
export function useRenderTracker(name: string, watched?: () => Record<string, unknown>): void {
  if (!recorder) return;
  recorder.recordRender(name, watched ? watched() : undefined);
}

/**
 * Wrap a subtree to capture its React commit duration under `id`. When the probe
 * is disabled this renders children directly (no Profiler node in the tree).
 * Commit durations require a profiling-enabled bundle (CRA `build --profile`);
 * render counts via useRenderTracker work in any build.
 */
export const ProfiledRegion: React.FC<{ id: string; children: React.ReactNode }> = ({
  id,
  children,
}) => {
  const r = recorder;
  if (!r) return <>{children}</>;
  return (
    <React.Profiler id={id} onRender={(_id, _phase, actualDuration) => r.recordCommit(id, actualDuration)}>
      {children}
    </React.Profiler>
  );
};
